import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';

import { useWindowSizeDebounced } from 'hooks/useWindowSize';
import { useFreecodeContextRun } from 'hooks/useContext';
import { AbsoluteSpinner } from 'components/Spinner/Spinner';
import { getPixiComponent } from './pixiComponent';
import addFreecodeGraphics from './addFreecodeGraphics';
import FreecodePlayerHeader from './FreecodePlayerHeader/freecodePlayerHeader';
import FreecodePlayerFooter from './FreecodePlayerFooter/freecodePlayerFooter';
import FreecodePlayerInteract from './freecodePlayerInteract';
import { FreecodePlayerBackground, FreecodePlayerContainer, CanvasContainer, CanvasHolder, PixiCanvas } from './styles';

const GlobalPixiComponent = getPixiComponent();

const interactStartingLog = [
  { log: [{ tag: null, index: null, pipe: 'out', message: 'Getting ready to start interact mode...' }], graphics: [] },
];

Math.clamp = function (value, min, max) {
  return Math.min(Math.max(value, min), max);
};

// If a custom PixiComponent is passed, then it must never change
// Make sure to store it in a context/scope that is preserved on rerender

export function FreecodePlayer({
  testImplementation,
  levels,
  levelToRun,
  setLevelToRun,
  sizeLimitByParent,
  isDarkMode,
  playByDefault,
  humanInputProps,
  setCurrentStepArg,
  setConsoleLogSteps,
  isFlowTab,
  PixiComponent = GlobalPixiComponent,
  updateInteractResult,
}) {
  const [canvasDimensions, setCanvasDimensions] = useState({});
  const [isRunButtonActive, setIsRunButtonActive] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const [interactLoading, setInteractLoading] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [isPopOut, setIsPopOut] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [buttonsDisabled, setButtonsDisabled] = useState(true);

  const windowSize = useWindowSizeDebounced();

  // instance variable refs
  const isPlayingRef = useRef(false);
  const stepTimeRef = useRef(testImplementation?.configCanvas?.stepTime ?? 400);
  const levelStepsRef = useRef(levels?.[0]?.steps);
  const gameIntervalRef = useRef(null);
  const gamePauseTimeRef = useRef(null);
  const startPlayingTimeRef = useRef(0);
  const currentStepRef = useRef(0);
  const pixelsPerUnitRef = useRef(null);
  const interactSessionStateRef = useRef(null);
  const restartInteractCommandRef = useRef(null);
  const interactRestartingRef = useRef(false);

  // Elements refs
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);

  const PixiComponentRef = useRef(PixiComponent);
  const canvasIdRef = useRef('canvas-' + [...Array(5)].map(() => Math.random().toString(36)[2]).join(''));

  const { isCanvasAnimationCompleted, setIsCanvasAnimationCompleted } = useFreecodeContextRun() ?? {};

  useEffect(() => {
    stepTimeRef.current = interacting
      ? testImplementation?.configCanvas?.stepTimeHumanInput
      : testImplementation?.configCanvas?.stepTime;
  }, [interacting, testImplementation?.configCanvas]);

  useEffect(() => {
    PixiComponentRef.current = PixiComponent;
  }, [PixiComponent]);

  useEffect(() => {
    setButtonsDisabled(levels?.[levelToRun]?.steps?.length === 1);
  }, [levels, levelToRun]);

  const calculateCanvasDimensions = useCallback(() => {
    if (!testImplementation) return;
    let canvasDimensionHeight = testImplementation.configCanvas.height;
    let canvasDimensionWidth = testImplementation.configCanvas.width;

    let windowWidth = windowSize.width || window.innerWidth;
    let windowHeight = windowSize.height || window.innerHeight;

    // default static size
    let maxWidth = windowWidth * 0.5;
    let maxHeight = windowHeight * 0.5;
    if (isPopOut) {
      // Canvas will expand and cover screen
      maxWidth = windowWidth * 0.9;
      maxHeight = windowHeight - 250;
    } else if (sizeLimitByParent) {
      // Player size is limited by size of players parent element
      maxWidth = containerRef.current.parentElement.offsetWidth;
      maxHeight =
        containerRef.current.parentElement.offsetHeight -
        headerRef.current.offsetHeight -
        footerRef.current.offsetHeight;
    }
    pixelsPerUnitRef.current = Math.min(maxWidth / canvasDimensionWidth, maxHeight / canvasDimensionHeight);

    setCanvasDimensions({
      width: pixelsPerUnitRef.current * canvasDimensionWidth,
      height: pixelsPerUnitRef.current * canvasDimensionHeight,
    });
  }, [windowSize, isPopOut, testImplementation, sizeLimitByParent]);

  // Used when updating progress with mouse
  const updateProgressBarByPercentage = useCallback(
    (percentage) => {
      const stepsLength = levelStepsRef.current?.length;
      if (stepsLength <= 1) return;
      setProgressPercentage(100 * percentage);
      let realStep = percentage * (stepsLength - 2);
      let [step, stepDiff, realPart] = [0, 0, 0];

      if (realStep < 1) {
        step = 0;
        if (realStep === 0) {
          realPart = 0;
          stepDiff = -currentStepRef.current;
        } else {
          realPart = realStep;
          stepDiff = -currentStepRef.current + 1;
        }
      } else {
        step = Math.floor(realStep);
        if (step > stepsLength - 2) {
          step = stepsLength - 2;
        }
        if (step === stepsLength - 2) {
          realPart = 1;
          stepDiff = step - currentStepRef.current;
        } else {
          realPart = realStep % step;
          stepDiff = step - currentStepRef.current + 1;
        }
      }
      if (stepDiff !== 0) {
        currentStepRef.current += stepDiff;
        PixiComponentRef.current.setLevelAndStep(levelToRun, currentStepRef.current);
        if (setCurrentStepArg) setCurrentStepArg(currentStepRef.current);
      }

      PixiComponentRef.current.setTween(realPart);
    },
    [levelToRun, setCurrentStepArg]
  );

  // Used for play and step forward
  const updateProgressBarFillRateWithRegardsToTime = useCallback(() => {
    let t = Math.clamp((Date.now() - startPlayingTimeRef.current) / stepTimeRef.current, 0, 1);
    let x = (t + currentStepRef.current - 1) / (levelStepsRef.current.length - 2);
    setProgressPercentage(100 * x);
  }, [startPlayingTimeRef, stepTimeRef, currentStepRef]);

  // Used for step backwards
  const updateProgressBarFillRateExplicitly = (part) => {
    let t = Math.clamp(part, 0, 1);
    let x = (t + currentStepRef.current - 1) / (levelStepsRef.current.length - 2);
    setProgressPercentage(100 * x);
  };

  const canPlayForward = useCallback(() => {
    return currentStepRef.current < levels[levelToRun].steps.length - 2;
  }, [levels, levelToRun]);

  const pauseCanvasTimer = useCallback(
    (t) => {
      if (!isRunButtonActive) {
        clearTimeout(gamePauseTimeRef.current);
        gamePauseTimeRef.current = setTimeout(function () {
          isPlayingRef.current = false;
        }, t);
      }
    },
    [isRunButtonActive]
  );

  const stepForward = useCallback(() => {
    if (canPlayForward()) {
      currentStepRef.current += 1;
      if (setCurrentStepArg) setCurrentStepArg(currentStepRef.current);
      isPlayingRef.current = true;
      startPlayingTimeRef.current = Date.now();
      PixiComponentRef.current.setLevelAndStep(levelToRun, currentStepRef.current);
      pauseCanvasTimer(stepTimeRef.current);
    } else {
      if (!isCanvasAnimationCompleted && setIsCanvasAnimationCompleted) setIsCanvasAnimationCompleted(true);
      setIsRunButtonActive(false);
      clearInterval(gameIntervalRef.current);
      PixiComponentRef.current.setTween(1);
      updateProgressBarFillRateExplicitly(1);
    }
  }, [
    canPlayForward,
    pauseCanvasTimer,
    levelToRun,
    setCurrentStepArg,
    isCanvasAnimationCompleted,
    setIsCanvasAnimationCompleted,
  ]);

  const stepBackward = () => {
    if (currentStepRef.current >= 1) {
      currentStepRef.current -= 1;
      if (setCurrentStepArg) setCurrentStepArg(currentStepRef.current);
      PixiComponentRef.current.setLevelAndStep(levelToRun, currentStepRef.current);
      startPlayingTimeRef.current = Date.now() + stepTimeRef.current;
      PixiComponentRef.current.setTween(1);
      updateProgressBarFillRateExplicitly(1);
    }
  };

  const resetLevels = useCallback(() => {
    levelStepsRef.current = levels[levelToRun].steps;
    PixiComponentRef.current.setLevelAndStep(levelToRun, currentStepRef.current);
    PixiComponentRef.current.setTween(1);
    updateProgressBarFillRateWithRegardsToTime();
  }, [levels, levelToRun, updateProgressBarFillRateWithRegardsToTime]);

  const loadLevels = useCallback(
    async (keepStates) => {
      if (!keepStates) PixiComponentRef.current.resetObjectStates();
      await PixiComponentRef.current.preprocessLevelOutput(levels, pixelsPerUnitRef.current);
      resetLevels();
      if (canPlayForward()) setIsRunButtonActive(true);
    },
    [levels, canPlayForward, resetLevels]
  );

  const tickerFunction = useCallback(() => {
    if (isPlayingRef.current) {
      PixiComponentRef.current.setTween(
        Math.clamp((Date.now() - startPlayingTimeRef.current) / stepTimeRef.current, 0, 1)
      );
      updateProgressBarFillRateWithRegardsToTime();
    }
  }, [updateProgressBarFillRateWithRegardsToTime]);

  useEffect(() => {
    return () => {
      // cleanup when leaving component
      clearInterval(gameIntervalRef.current);
      isPlayingRef.current = false;
      PixiComponentRef.current.ticker.remove(tickerFunction);
    };
  }, [tickerFunction]);

  useEffect(() => {
    if (!testImplementation || allLoaded) return;
    stepTimeRef.current = testImplementation.configCanvas?.stepTime ?? 400;

    PixiComponentRef.current.initialize(
      {
        isDarkMode,
        resizeTo: window,
        resolution: 1,
        antialias: true,
        backgroundColor: testImplementation.configCanvas.background,
        view: document.getElementById(canvasIdRef.current),
      },
      tickerFunction
    );

    const addGraphics = async () => {
      for (const graphicData of testImplementation.graphics)
        await addFreecodeGraphics(graphicData, PixiComponentRef.current);
      setAllLoaded(true);
    };

    addGraphics();
  }, [testImplementation, allLoaded, isDarkMode, tickerFunction]);

  useEffect(() => {
    if (!allLoaded) return;
    if (isDarkMode === PixiComponentRef.current.isDarkMode) return;
    PixiComponentRef.current.setDarkMode(isDarkMode, levels);
  }, [allLoaded, isDarkMode, levels]);

  useEffect(() => {
    if (!allLoaded || !testImplementation?.configCanvas?.background) return;
    PixiComponentRef.current.setBackgroundColor(testImplementation.configCanvas.background);
  }, [allLoaded, testImplementation?.configCanvas?.background]);

  useEffect(() => {
    if (!allLoaded) return;
    calculateCanvasDimensions();
    PixiComponentRef.current.setScaleAndResolution(null, pixelsPerUnitRef.current);
  }, [allLoaded, calculateCanvasDimensions]);

  useEffect(() => {
    if (!allLoaded || !levels?.length) return;
    // All loaded or levels changed
    currentStepRef.current = 0;
    if (!testImplementation?.configCanvas?.canvasAnimated) {
      currentStepRef.current = levels.length - 1;
    }
    if (setCurrentStepArg) setCurrentStepArg(currentStepRef.current);
    levelStepsRef.current = levels[levelToRun].steps;
    clearInterval(gameIntervalRef.current);
    setIsRunButtonActive(false);
    loadLevels();
  }, [
    allLoaded,
    levels,
    levelToRun,
    playByDefault,
    loadLevels,
    setCurrentStepArg,
    testImplementation?.configCanvas?.canvasAnimated,
  ]);

  useEffect(() => {
    if (isRunButtonActive) {
      if (!canPlayForward()) {
        currentStepRef.current = 0;
        if (setCurrentStepArg) setCurrentStepArg(currentStepRef.current);
        resetLevels();
      }
      clearTimeout(gamePauseTimeRef.current);
      gameIntervalRef.current = setInterval(() => {
        stepForward();
      }, stepTimeRef.current);
    } else {
      pauseCanvasTimer(0);
      clearInterval(gameIntervalRef.current);
    }
  }, [isRunButtonActive, canPlayForward, pauseCanvasTimer, resetLevels, stepForward, setCurrentStepArg]);

  const selectLevel = (index) => {
    if (interacting) return;

    clearInterval(gameIntervalRef.current);
    pauseCanvasTimer(0);
    startPlayingTimeRef.current = 0;
    currentStepRef.current = 0;
    if (setCurrentStepArg) setCurrentStepArg(currentStepRef.current);
    setIsRunButtonActive(false);
    setLevelToRun && setLevelToRun(index);
  };

  const startHumanInput = useCallback(
    (restartCommand = null) => {
      setInteractLoading(true);
      currentStepRef.current = 0;
      if (setCurrentStepArg) setCurrentStepArg(currentStepRef.current);
      if (setConsoleLogSteps) setConsoleLogSteps(interactStartingLog);
      if (restartCommand) restartInteractCommandRef.current = restartCommand;

      setButtonsDisabled(false);
      setInteracting(true);
    },
    [setCurrentStepArg, setConsoleLogSteps]
  );

  const stopHumanInput = useCallback(() => {
    if (!interactLoading) setInteracting(false);
    PixiComponentRef.current.deregisterKeyboardEventListeners();
  }, [interactLoading]);

  const restartHumanInput = () => {
    interactSessionStateRef.current = null;
    restartInteractCommandRef.current = null;

    if (interacting) {
      interactRestartingRef.current = true;
      stopHumanInput();
      setTimeout(() => {
        startHumanInput();
      }, 10);
    } else startHumanInput();
  };

  const handleInteractResponse = useCallback(
    (steps, stopInteracting, interactSessionState) => {
      if (interactRestartingRef.current) {
        interactRestartingRef.current = false;
        return;
      }

      if (stopInteracting) stopHumanInput();

      interactSessionStateRef.current = interactSessionState;
      restartInteractCommandRef.current = null;

      levels[levelToRun].steps = [...steps];

      if (PixiComponent.childToReRender) {
        currentStepRef.current = currentStepRef.current + 1;
        if (setCurrentStepArg) setCurrentStepArg(currentStepRef.current);
      }
      if (setConsoleLogSteps) setConsoleLogSteps([...steps, { log: [], graphics: [] }]);

      loadLevels(true);
      if (!PixiComponentRef.current.hasRestartEvent()) setInteractLoading(false);
    },
    [
      interactRestartingRef,
      levels,
      levelToRun,
      setConsoleLogSteps,
      loadLevels,
      stopHumanInput,
      PixiComponent,
      setCurrentStepArg,
    ]
  );

  const handleInteractAction = (option) => {
    const action = option.action;
    switch (action) {
      case 'start':
        startHumanInput();
        break;
      case 'stop':
        stopHumanInput();
        break;
      case 'restart':
        restartHumanInput();
        break;
      case 'reset':
        localStorage.removeItem(`fsEditor-${testImplementation.test}-interactSettingState`);
        restartHumanInput();
        break;
      default:
    }
  };

  const tabValues = useMemo(() => {
    if (!allLoaded) return [{ label: 'Loading...' }];

    if (isFlowTab) return [];

    if (levels.length <= testImplementation.configLevels.numPublic) {
      return levels.map((item, index) => ({ label: 'Level ' + (index + 1) }));
    }

    const getIcon = (levelIndex) => {
      let isPublic = levelIndex < testImplementation.configLevels.numPublic;
      return isPublic ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash';
    };

    return levels.map((item, index) => ({ label: `Level ${index + 1}`, icon: getIcon(index) }));
  }, [allLoaded, testImplementation?.configLevels?.numPublic, levels, isFlowTab]);

  const renderFreecodePlayerHeader = () => {
    return (
      <FreecodePlayerHeader
        ref={headerRef}
        items={tabValues}
        active={levelToRun}
        setActive={selectLevel}
        humanInput={
          testImplementation?.configCanvas?.canvasHumanInput &&
          testImplementation?.configCanvas?.interactionByLevel?.[levelToRun] &&
          humanInputProps
        }
        interacting={interacting}
        startHumanInput={startHumanInput}
        stopHumanInput={stopHumanInput}
        interactSessionState={interactSessionStateRef.current}
        handleInteractAction={handleInteractAction}
        isPopOut={isPopOut}
        setIsPopOut={setIsPopOut}
        allLoaded={allLoaded}
      />
    );
  };

  const renderFreecodePlayerInteract = () => (
    <FreecodePlayerInteract
      humanInputProps={humanInputProps}
      levelToRun={levelToRun}
      interactSessionState={interactSessionStateRef.current}
      restartInteractCommand={restartInteractCommandRef.current}
      handleInteractResponse={handleInteractResponse}
      startHumanInput={startHumanInput}
      PixiComponent={PixiComponent}
      updateInteractResult={updateInteractResult}
    />
  );

  const renderFreecodePlayerFooter = () => (
    <FreecodePlayerFooter
      ref={footerRef}
      loading={!allLoaded}
      testImplementation={testImplementation}
      buttonsDisabled={buttonsDisabled}
      isRunButtonActive={isRunButtonActive}
      setIsRunButtonActive={setIsRunButtonActive}
      stepForward={stepForward}
      stepBackward={stepBackward}
      progressPercentage={progressPercentage}
      updateProgressBarByPercentage={updateProgressBarByPercentage}
    />
  );

  return (
    <FreecodePlayerBackground ref={containerRef} isPopOut={isPopOut}>
      <FreecodePlayerContainer id="freecodePlayer" isPopOut={isPopOut}>
        {renderFreecodePlayerHeader()}
        {interacting && renderFreecodePlayerInteract()}
        <CanvasContainer>
          <AbsoluteSpinner
            width={canvasDimensions.width + 'px'}
            height={canvasDimensions.height + 'px'}
            backgroundOverlay
            loading={interactLoading}
          />
          <CanvasHolder height={canvasDimensions.height} width={canvasDimensions.width}>
            <PixiCanvas id={canvasIdRef.current} />
          </CanvasHolder>
        </CanvasContainer>
        {renderFreecodePlayerFooter()}
      </FreecodePlayerContainer>
    </FreecodePlayerBackground>
  );
}
